import {ExperimentNames} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useExperiments} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {useEventDateInformation} from '../../../../../../commons/hooks/dates'
import {useSettings} from '../../../../hooks/settings'
import {useVisibilityStyles} from '../../../../hooks/use-visibility-styles'
import {settingsParams} from '../../../../settingsParams'
import {ClosedRegistrationButton} from '../../registration-buttons/closed-registration-button'
import {EditRegistrationButton} from '../../registration-buttons/edit-registration-button'
import {RegistrationButtonSkin} from '../../registration-buttons/registration-button-skin'
import {TicketsUnavailableButton} from '../../registration-buttons/tickets-unavailable-button'
import {CustomDatesLabel} from '../custom-dates-label'
import s from './registration-button.scss'
import {RegistrationButtonProps} from '.'

export const RegistrationButton = ({
  disableSpacing,
  ticketedEvent,
  ticketedOrInitiallyTicketedEvent,
  registrationClosed,
  memberRsvpExists,
  onClick,
  imageVisible,
  isMobile,
  ticketsUnavailable,
  className,
  url,
  isExternalLink,
  withoutRegistration,
  registrationScheduled,
  registrationWithCustomDates,
  eventId,
  t,
  eventListPageUrl,
  narrowerButton,
}: RegistrationButtonProps) => {
  const {formattedRegistrationStartDate: startDate, formattedRegistrationEndDate: endDate} =
    useEventDateInformation(eventId)
  const {
    allBreakpoints: {isHeaderRegistrationButtonVisible},
  } = useVisibilityStyles()
  const settings = useSettings()
  const {experiments} = useExperiments()

  const ticketed = experiments.enabled(ExperimentNames.RsvpButtonContentUpdates)
    ? ticketedOrInitiallyTicketedEvent
    : ticketedEvent
  const rsvpButtonText = settings.getWithFallback(
    ticketed ? settingsParams.ticketedEventButtonText : settingsParams.rsvpEventButtonText,
    settingsParams.rsvpButtonText,
  )

  if (!isHeaderRegistrationButtonVisible()) {
    return null
  }

  const getButton = () => {
    if (registrationScheduled) {
      return (
        <>
          <ClosedRegistrationButton href={eventListPageUrl} />
          <CustomDatesLabel text={t('registration.opensLabel', {date: startDate})} dataHook={DH.RSVP_OPENS_LABEL} />
        </>
      )
    }

    if (ticketsUnavailable) {
      return <TicketsUnavailableButton href={eventListPageUrl} />
    }

    if (registrationClosed) {
      return <ClosedRegistrationButton href={eventListPageUrl} />
    }

    if (memberRsvpExists) {
      return <EditRegistrationButton />
    }

    if (withoutRegistration) {
      return null
    }

    return (
      <>
        <RegistrationButtonSkin
          text={rsvpButtonText}
          onClick={onClick}
          url={url}
          isExternalLink={isExternalLink}
          dataHook={ticketed ? DH.HEADER_GET_TICKETS_BUTTON : DH.RSVP_BUTTON}
          narrowerButton={narrowerButton}
        />
        {registrationWithCustomDates && (
          <CustomDatesLabel text={t('registration.closesLabel', {date: endDate})} dataHook={DH.RSVP_CLOSES_LABEL} />
        )}
      </>
    )
  }

  const button = getButton()

  return button ? (
    <ButtonContainer
      hasImageButtonSpacing={isMobile || imageVisible}
      disableSpacing={disableSpacing}
      className={classNames(s.container, className)}
    >
      {button}
    </ButtonContainer>
  ) : null
}

interface ButtonContainerProps {
  disableSpacing: boolean
  hasImageButtonSpacing: boolean
  className: string
}

const ButtonContainer: React.FC<ButtonContainerProps> = ({
  hasImageButtonSpacing,
  disableSpacing,
  className,
  children,
}) => {
  const spacingClass = hasImageButtonSpacing ? s.registrationButtonSpacing : s.registrationButtonSpacingNoImage
  return <div className={classNames({[spacingClass]: !disableSpacing}, className)}>{children}</div>
}
